import React, { useState, useEffect } from 'react';
import { ArrowLeft, FileDown, Package, DollarSign, TrendingUp, Calendar, Download, Edit2, Trash2, X, Check, CheckCircle, XCircle, Search } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import apiService from '../services/api';

const Toast = ({ message, type, onClose }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const duration = 4000;
    const interval = 10;
    const decrement = (100 * interval) / duration;

    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(progressTimer);
          return 0;
        }
        return prev - decrement;
      });
    }, interval);

    const closeTimer = setTimeout(() => {
      onClose();
    }, duration);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(closeTimer);
    };
  }, [onClose]);

  const config = {
    success: {
      icon: <CheckCircle size={22} />,
      bgColor: 'bg-gradient-to-r from-green-500 to-green-600',
      progressColor: 'bg-green-300'
    },
    error: {
      icon: <XCircle size={22} />,
      bgColor: 'bg-gradient-to-r from-red-500 to-red-600',
      progressColor: 'bg-red-300'
    }
  };

  const currentConfig = config[type] || config.success;

  return (
    <div className={`${currentConfig.bgColor} text-white rounded-xl shadow-2xl overflow-hidden min-w-[320px] animate-slideIn`}>
      <div className="px-5 py-4 flex items-start gap-3">
        <div className="flex-shrink-0">{currentConfig.icon}</div>
        <div className="flex-1">
          <p className="font-medium text-sm">{message}</p>
        </div>
        <button onClick={onClose} className="hover:bg-white/20 rounded p-1">
          <X size={18} />
        </button>
      </div>
      <div className="h-1 bg-black/10">
        <div className={`h-full ${currentConfig.progressColor} transition-all`} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
};

const ToastContainer = ({ toasts, removeToast }) => (
  <div className="fixed top-4 right-4 z-50 space-y-3">
    {toasts.map((toast) => (
      <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
    ))}
  </div>
);

const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full animate-slideIn">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="px-6 py-4">
          <p className="text-gray-600">{message}</p>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
};

function ClienteDetalle({ clienteNombre, onBack }) {
  const [registros, setRegistros] = useState([]);
  const [stats, setStats] = useState(null);
  const [demorasPorMes, setDemorasPorMes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [formData, setFormData] = useState({
    folio: '',
    master: '',
    contenedor: '',
    cliente: '',
    naviera: '',
    ata: '',
    ultimoDiaSinDemoras: '',
    diasIncurridos: '',
    fechaVacio: ''
  });

  useEffect(() => {
    fetchClienteData();
  }, [clienteNombre]);

  const fetchClienteData = async () => {
    try {
      setLoading(true);
      const encodedNombre = encodeURIComponent(clienteNombre);
      const [registrosData, statsData, demorasData] = await Promise.all([
        apiService.get(`/clientes/${encodedNombre}/registros`),
        apiService.get(`/clientes/${encodedNombre}/estadisticas`),
        apiService.get(`/clientes/${encodedNombre}/demoras-mes`)
      ]);

      setRegistros(registrosData);
      setStats(statsData);
      
      const formatearMes = (mesString) => {
        if (!mesString) return 'Sin fecha';
        
        const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        
        try {
          const [year, month] = mesString.split('-');
          if (!year || !month) return 'Sin fecha';
          return `${meses[parseInt(month) - 1]} ${year}`;
        } catch (error) {
          console.error('Error al formatear mes:', mesString);
          return 'Fecha inválida';
        }
      };
      
      const demorasFormateadas = demorasData
        .filter(d => d.mes && d.mes !== null)
        .map(d => ({
          mes: formatearMes(d.mes),
          contenedores: parseInt(d.totalContenedores) || 0,
          totalDias: parseInt(d.totalDias) || 0,
          promedioDias: parseFloat(d.promedioDias).toFixed(1)
        }))
        .reverse();

      setDemorasPorMes(demorasFormateadas);
      
    } catch (error) {
      console.error('Error al cargar datos del cliente:', error);
      showToast('Error al cargar datos del cliente', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const dataToExport = registros.map(r => ({
        'FOLIO': r.folio,
        'MASTER': r.master,
        'CONTENEDOR': r.contenedor,
        'CLIENTE': r.cliente,
        'NAVIERA': r.naviera,
        'ATA': r.ata || 'Sin fecha',
        'ULTIMO DIA SIN DEMORAS': r.ultimoDiaSinDemoras || 'Sin fecha',
        'DIAS INCURRIDOS': r.diasIncurridos,
        'FECHA DE VACIO': r.fechaVacio || 'Sin fecha',
        'COSTO NAVIERA BASE': r.costoNavieraSinIVA?.toFixed(2) || '0.00',
        'COSTO NAVIERA TOTAL': r.costoNavieraConIVA?.toFixed(2) || '0.00',
        'VENTA AL CLIENTE BASE': r.ventaClienteSinIVA?.toFixed(2) || '0.00',
        'VENTA AL CLIENTE TOTAL': r.ventaClienteConIVA?.toFixed(2) || '0.00'
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, clienteNombre);
      XLSX.writeFile(wb, `${clienteNombre}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      showToast('📊 Excel exportado exitosamente', 'success');
    } catch (error) {
      showToast('❌ Error al exportar Excel', 'error');
    }
  };

  const exportToPDF = async () => {
    try {
      const jsPDF = (await import('jspdf')).default;
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF('l', 'mm', 'a4');
      
      doc.setFontSize(18);
      doc.text(`Reporte de ${clienteNombre}`, 14, 15);
      doc.setFontSize(12);
      doc.text(`Total Contenedores: ${stats?.totalContenedores || 0}`, 14, 25);
      doc.text(`Utilidad: $${stats?.utilidad.toFixed(2) || '0.00'}`, 14, 32);
      
      const tableData = registros.map(r => [
        r.folio,
        r.contenedor,
        r.naviera,
        r.diasIncurridos,
        `$${r.costoNavieraConIVA?.toFixed(2) || '0.00'}`,
        `$${r.ventaClienteConIVA?.toFixed(2) || '0.00'}`
      ]);

      autoTable(doc, {
        head: [['Folio', 'Contenedor', 'Naviera', 'Días', 'Costo Naviera', 'Venta Cliente']],
        body: tableData,
        startY: 40,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [239, 68, 68] }
      });

      doc.save(`${clienteNombre}_${new Date().toISOString().slice(0, 10)}.pdf`);
      showToast('📄 PDF exportado exitosamente', 'success');
    } catch (error) {
      showToast('❌ Error al exportar PDF', 'error');
    }
  };

  const openModal = (record) => {
    setEditingRecord(record);
    setFormData({
      folio: record.folio,
      master: record.master,
      contenedor: record.contenedor,
      cliente: record.cliente,
      naviera: record.naviera,
      ata: record.ata || '',
      ultimoDiaSinDemoras: record.ultimoDiaSinDemoras || '',
      diasIncurridos: record.diasIncurridos,
      fechaVacio: record.fechaVacio || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      await apiService.put(`/registros/${editingRecord.id}`, formData);
      showToast('✅ Registro actualizado exitosamente', 'success');
      fetchClienteData();
      setShowModal(false);
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast('❌ Error al conectar con el servidor', 'error');
      }
    }
  };

  const handleDeleteClick = (id) => {
    setRecordToDelete(id);
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const result = await apiService.delete(`/registros/${recordToDelete}`);

      if (result.clienteEliminado) {
        showToast(`Cliente "${result.clienteEliminado}" eliminado (sin contenedores restantes)`, 'success');
        // Volver a la lista de clientes ya que el cliente fue eliminado
        setTimeout(() => onBack(), 1500);
      } else {
        showToast('Registro eliminado exitosamente', 'success');
        fetchClienteData();
      }
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast('Error al conectar con el servidor', 'error');
      }
    }

    setShowConfirmDialog(false);
    setRecordToDelete(null);
  };

  const filteredRegistros = registros.filter(registro =>
    Object.values(registro).some(value =>
      value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRegistros.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRegistros.length / itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando datos del cliente...</p>
        </div>
      </div>
    );
  }

  const chartData = registros.slice(0, 10).map(r => ({
    nombre: r.contenedor.substring(0, 8),
    costo: r.costoNavieraConIVA,
    venta: r.ventaClienteConIVA,
    utilidad: r.ventaClienteConIVA - r.costoNavieraConIVA
  }));

  const pieData = [
    { name: 'Costos', value: stats?.totalCostoNaviera || 0, color: '#ef4444' },
    { name: 'Utilidad', value: stats?.utilidad || 0, color: '#10b981' }
  ];

  return (
    <div className="p-8">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Confirmar eliminación"
        message="¿Estás seguro de que deseas eliminar este registro?"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowConfirmDialog(false)}
      />

      <div className="mb-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4 transition"
        >
          <ArrowLeft size={20} />
          <span>Volver a clientes</span>
        </button>
        
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{clienteNombre}</h1>
          <p className="text-gray-600">Contenedores y estadísticas del cliente</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white border-l-4 border-ink-900 shadow-soft rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Package size={32} className="text-ink-700" />
          </div>
          <p className="text-ink-500 text-sm font-medium">Total Contenedores</p>
          <p className="text-3xl font-bold text-ink-900 mt-1">{stats?.totalContenedores || 0}</p>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-soft border-l-4 border-purple-500">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="text-purple-600" size={24} />
            <p className="text-ink-700 font-semibold">Días Totales</p>
          </div>
          <p className="text-3xl font-bold text-ink-900">{stats?.totalDiasIncurridos || 0}</p>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-soft border-l-4 border-green-500">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="text-green-600" size={24} />
            <p className="text-ink-700 font-semibold">Ingresos Totales</p>
          </div>
          <p className="text-3xl font-bold text-ink-900">
            ${stats?.totalVentaCliente.toFixed(2) || '0.00'}
          </p>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-soft border-l-4 border-orange-500">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="text-orange-600" size={24} />
            <p className="text-ink-700 font-semibold">Utilidad</p>
          </div>
          <p className="text-3xl font-bold text-ink-900">
            ${stats?.utilidad.toFixed(2) || '0.00'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2">
          <h3 className="text-lg font-bold text-gray-800 mb-4">📅 Demoras por Mes (basado en ATA)</h3>
          {demorasPorMes.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={demorasPorMes}>
                <defs>
                  <linearGradient id="colorContenedores" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorDias" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="contenedores" stroke="#3b82f6" fillOpacity={1} fill="url(#colorContenedores)" name="Contenedores" />
                <Area type="monotone" dataKey="totalDias" stroke="#ef4444" fillOpacity={1} fill="url(#colorDias)" name="Total Días" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Calendar size={48} className="mx-auto mb-3 text-gray-300" />
              <p>No hay datos de demoras por mes</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Costos vs Ventas</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nombre" />
              <YAxis />
              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
              <Legend />
              <Bar dataKey="costo" fill="#ef4444" name="Costo" />
              <Bar dataKey="venta" fill="#10b981" name="Venta" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Card de Utilidad - Ocultada
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Utilidad</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nombre" />
              <YAxis />
              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
              <Legend />
              <Line type="monotone" dataKey="utilidad" stroke="#3b82f6" strokeWidth={2} name="Utilidad" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        */}

        {/* Card de Distribución - Ocultada
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Distribución</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        */}

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Resumen Financiero</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Total Costos</span>
              <span className="text-lg font-bold text-red-600">${stats?.totalCostoNaviera.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Total Ingresos</span>
              <span className="text-lg font-bold text-green-600">${stats?.totalVentaCliente.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
              <span className="text-gray-700 font-semibold">Utilidad Neta</span>
              <span className="text-2xl font-bold text-blue-600">${stats?.utilidad.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Margen</span>
              <span className="text-lg font-bold text-purple-600">
                {stats?.totalVentaCliente > 0
                  ? `${((stats.utilidad / stats.totalVentaCliente) * 100).toFixed(1)}%`
                  : '0%'
                }
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar contenedores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">Contenedores de {clienteNombre}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Folio</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Master</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Contenedor</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Naviera</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase">ATA</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Días</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Costo Naviera</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Venta Cliente</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Utilidad</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase sticky right-0 bg-gray-50">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length > 0 ? (
                currentItems.map((registro) => (
                  <tr key={registro.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-xs text-gray-900">{registro.folio || '-'}</td>
                    <td className="px-3 py-3 text-xs text-gray-900">{registro.master || '-'}</td>
                    <td className="px-3 py-3 text-xs text-gray-900 font-medium">{registro.contenedor || '-'}</td>
                    <td className="px-3 py-3 text-xs text-gray-900">{registro.naviera || '-'}</td>
                    <td className="px-3 py-3 text-xs text-gray-900">{registro.ata || 'Sin fecha'}</td>
                    <td className="px-3 py-3 text-xs text-gray-900 font-medium">{registro.diasIncurridos || 0}</td>
                    <td className="px-3 py-3 text-xs text-red-600 font-bold">${registro.costoNavieraConIVA?.toFixed(2) || '0.00'}</td>
                    <td className="px-3 py-3 text-xs text-green-600 font-bold">${registro.ventaClienteConIVA?.toFixed(2) || '0.00'}</td>
                    <td className="px-3 py-3 text-xs text-blue-600 font-bold">
                      ${((registro.ventaClienteConIVA || 0) - (registro.costoNavieraConIVA || 0)).toFixed(2)}
                    </td>
                    <td className="px-3 py-3 text-center sticky right-0 bg-white">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => openModal(registro)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Editar"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(registro.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"

                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="px-4 py-12 text-center text-gray-500">
                    <Package size={48} className="mx-auto text-gray-300 mb-3" />
                    <p>No hay contenedores para este cliente</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-white">
            <div className="text-sm text-gray-600">
              Mostrando <span className="font-semibold">{indexOfFirstItem + 1}</span> - <span className="font-semibold">{Math.min(indexOfLastItem, filteredRegistros.length)}</span> de <span className="font-semibold">{filteredRegistros.length}</span> registros
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <div className="flex gap-1">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === i + 1
                        ? 'bg-red-600 text-white shadow-sm'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold">Editar Registro</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Folio</label>
                  <input
                    type="text"
                    value={formData.folio}
                    onChange={(e) => setFormData({ ...formData, folio: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Master</label>
                  <input
                    type="text"
                    value={formData.master}
                    onChange={(e) => setFormData({ ...formData, master: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contenedor</label>
                  <input
                    type="text"
                    value={formData.contenedor}
                    onChange={(e) => setFormData({ ...formData, contenedor: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                  <input
                    type="text"
                    value={formData.cliente}
                    onChange={(e) => setFormData({ ...formData, cliente: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Naviera</label>
                  <input
                    type="text"
                    value={formData.naviera}
                    onChange={(e) => setFormData({ ...formData, naviera: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ATA</label>
                  <input
                    type="text"
                    value={formData.ata}
                    onChange={(e) => setFormData({ ...formData, ata: e.target.value })}
                    placeholder="DD/MM/YYYY"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Último Día Sin Demoras</label>
                  <input
                    type="text"
                    value={formData.ultimoDiaSinDemoras}
                    onChange={(e) => setFormData({ ...formData, ultimoDiaSinDemoras: e.target.value })}
                    placeholder="DD/MM/YYYY"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Días Incurridos *</label>
                  <input
                    type="number"
                    value={formData.diasIncurridos}
                    onChange={(e) => setFormData({ ...formData, diasIncurridos: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                    placeholder="Ingrese los días"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Vacío</label>
                  <input
                    type="text"
                    value={formData.fechaVacio}
                    onChange={(e) => setFormData({ ...formData, fechaVacio: e.target.value })}
                    placeholder="DD/MM/YYYY"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-semibold mb-2">
                  📊 Cálculos Automáticos
                </p>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• <strong>Costo Naviera s/IVA:</strong> Días × $140</li>
                  <li>• <strong>Costo Naviera c/IVA:</strong> Costo s/IVA × 1.16</li>
                  <li>• <strong>Venta Cliente s/IVA:</strong> Días × $155</li>
                  <li>• <strong>Venta Cliente c/IVA:</strong> Venta s/IVA × 1.16</li>
                </ul>
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                <Check size={20} />
                <span>Guardar Cambios</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClienteDetalle;